import { IAction, ActionValidationMiddleware, IMarketingCloudUser } from '@ads/iam-library';
import { getCachedEnforcer } from './getCachedEnforcer';

type MakeOptional<Type, Key extends keyof Type> = Omit<Type, Key> & Partial<Pick<Type, Key>>;

export type SimpleDirectiveAction = [IAction['domainName'], IAction['resourceType']];
// ['iam', 'holding']
export type FullDirectiveAction = MakeOptional<Omit<IAction, 'userId'>, 'actionName'>;
// { domainName: 'iam', resourceType: 'holding', holdingId: 1, businessEntityId: 1, resourceId: 1, ownerId: 'abc' }

export type DirectiveAction = SimpleDirectiveAction | FullDirectiveAction;

/**
 * Accepts different action inputs (See DirectiveAction type)
 *  - Simple version: ['iam', 'holding'] (REQUIRES defaultAction to be set)
 *  - Full version: { domainName: 'iam', resourceType: 'holding', holdingId?: 1, businessEntityId?: 1, resourceId?: 1, ownerId?: 'abc' }
 */
export default class FrontendActionValidator {
    private readonly defaultAction?: IAction['actionName'];

    constructor(defaultAction?: IAction['actionName']) {
        this.defaultAction = defaultAction;
    }

    async validate(action: DirectiveAction, user: IMarketingCloudUser): Promise<boolean> {
        const iamAction = this.buildIamAction(user.getCognitoId(), action, this.defaultAction);
        const actionValidator = new ActionValidationMiddleware(await getCachedEnforcer(user));
        return actionValidator.validateAction(iamAction);
    }

    private buildIamAction(userId: string, action: DirectiveAction, defaultAction?: IAction['actionName']): IAction {
        if (!action) {
            throw new Error('No action provided.');
        }
        if (this.isSimplifiedAction(action) && !defaultAction) {
            throw new Error('No action name provided.');
        }

        if (this.isSimplifiedAction(action)) {
            return {
                userId,
                actionName: defaultAction,
                domainName: action[0],
                resourceType: action[1],
            };
        }

        if (!action.actionName && !defaultAction) {
            throw new Error('No action name provided.');
        }

        return {
            ...action,
            userId,
            actionName: defaultAction || action.actionName,
        };
    }

    private isSimplifiedAction(action: DirectiveAction): action is SimpleDirectiveAction {
        if (Array.isArray(action)) {
            if (action.length !== 2) {
                // eslint-disable-next-line max-len
                throw new Error(
                    `Invalid action provided. Syntax is ["domain", "resourceType"]. Given: ${JSON.stringify(action)}`,
                );
            }
            return true;
        }
        return false;
    }
}
